/**
 * KPI Grid Widget
 *
 * Displays key performance indicators in a grid layout
 * with target comparison and trend indicators.
 * Connected to live Dashboard API with mock fallback.
 *
 * @author BizClick WMS
 * @version 2.0.0
 */

import React from 'react';
import { TrendingUp, TrendingDown, Minus, Target, CheckCircle2, XCircle } from 'lucide-react';
import { WidgetInstance, KPIData } from '../../types';
import { getKPIs } from '../../mockDataService';
import { fetchKPIs } from '../../../../../../services/dashboardService';
import { useWidgetData } from '../../hooks/useWidgetData';
import WidgetWrapper from '../WidgetWrapper';
import { WidgetSkeleton } from '../shared';

interface KPIGridWidgetProps {
  instance: WidgetInstance;
}

const KPIGridWidget: React.FC<KPIGridWidgetProps> = ({ instance }) => {
  const { data, isLoading, error, refresh, isFirstLoad, isLive } = useWidgetData<KPIData[]>({
    widgetId: 'kpi-grid',
    instance,
    fetchFn: async () => {
      try {
        return await fetchKPIs();
      } catch {
        return getKPIs();
      }
    },
  });

  if (isFirstLoad) {
    return (
      <WidgetWrapper instance={instance} onRefresh={refresh} isLoading={true}>
        <WidgetSkeleton variant="stat" count={6} />
      </WidgetWrapper>
    );
  }

  const kpis = data ?? [];

  const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-green-400" />;
      case 'down':
        return <TrendingDown className="w-4 h-4 text-red-400" />;
      default:
        return <Minus className="w-4 h-4 text-gray-400" />;
    }
  };

  const meetsTarget = (kpi: KPIData) => {
    if (kpi.name.toLowerCase().includes('feil') || kpi.name.toLowerCase().includes('tid')) {
      return kpi.value <= kpi.target;
    }
    return kpi.value >= kpi.target;
  };

  return (
    <WidgetWrapper instance={instance} onRefresh={refresh} isLoading={isLoading} error={error?.message} isLive={isLive}>
      <div className="h-full grid grid-cols-3 gap-3">
        {kpis.map((kpi, index) => {
          const targetMet = meetsTarget(kpi);
          return (
            <div
              key={index}
              className={`
                bg-gray-700/30 rounded-lg p-3 flex flex-col
                border-l-4 ${targetMet ? 'border-green-500' : 'border-yellow-500'}
              `}
            >
              <div className="flex items-start justify-between mb-2">
                <span className="text-xs text-gray-400 leading-tight">{kpi.name}</span>
                <div className="flex items-center gap-1">
                  {getTrendIcon(kpi.trend)}
                </div>
              </div>

              <div className="flex-1 flex items-center">
                <span className="text-2xl font-bold text-white">
                  {typeof kpi.value === 'number' && kpi.value % 1 !== 0
                    ? kpi.value.toFixed(1)
                    : kpi.value}
                </span>
                <span className="text-sm text-gray-400 ml-1">{kpi.unit}</span>
              </div>

              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <Target className="w-3 h-3" />
                  <span>{kpi.target} {kpi.unit}</span>
                </div>
                {targetMet ? (
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                ) : (
                  <XCircle className="w-4 h-4 text-yellow-400" />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </WidgetWrapper>
  );
};

export default KPIGridWidget;
